import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { newFormatPrice } from '../services/utilityFunctions';
import translate from './translate';
import PriceProp from './propTypes/priceProp';

function PriceDisplay(props) {
  const {
    t,
    type,
    userPrice,
    userPromotionPrice,
    showFree,
    frequency,
    withWrapper = false,
  } = props;

  const {
    originalPriceWithVAT: priceTotalVAT,
    originalPriceWithoutVAT: priceTotalNoVAT,
    code: priceCode,
    highlightedPrice,
  } = userPrice || {};

  const {
    originalPriceWithVAT: promotionTotalVAT,
    originalPriceWithoutVAT: promotionTotalNoVAT,
    code: promotionCode,
  } = userPromotionPrice || {};

  // we should show the company view if user is company or if the user is outside europe
  const showPricesWithoutVAT = highlightedPrice === 'originalPriceWithoutVAT';
  const priceTotal = showPricesWithoutVAT ? priceTotalNoVAT : priceTotalVAT;
  const promotionTotal = showPricesWithoutVAT
    ? promotionTotalNoVAT
    : promotionTotalVAT;

  const isFree =
    (!userPrice && !userPromotionPrice) ||
    (priceTotal === 0 && (!userPromotionPrice || promotionTotal === 0)) ||
    promotionTotal === 0;

  const hasPromotion = promotionTotal !== null && promotionTotal >= 0;

  const previousPrice = useMemo(() => {
    if (priceTotal > 0) {
      return newFormatPrice(priceTotal, true, priceCode);
    }
    return '';
  }, [priceTotal, priceCode]);

  const currentPrice = useMemo(() => {
    let current = type ? `${t('free')} ${t(type)}` : t('free');
    if (
      priceTotal > 0 ||
      (!showFree && typeof priceTotal === 'number' && priceTotal >= 0)
    ) {
      current = newFormatPrice(priceTotal, true, priceCode);
    }
    if (
      promotionTotal > 0 ||
      (!showFree && typeof promotionTotal === 'number' && promotionTotal >= 0)
    ) {
      current = newFormatPrice(promotionTotal, true, promotionCode);
    }
    if (
      showFree &&
      typeof promotionTotal === 'number' &&
      promotionTotal === 0
    ) {
      current = type ? `${t('free')} ${t(type)}` : t('free');
    }
    return current;
  }, [userPrice, userPromotionPrice]);

  const minorTotal = useMemo(() => {
    const vatInfo = !showPricesWithoutVAT
      ? t('vat.excluded.abbreviation')
      : t('vat.included.abbreviation');
    if (!isFree && hasPromotion) {
      const value = newFormatPrice(
        showPricesWithoutVAT ? promotionTotalVAT : promotionTotalNoVAT,
        true,
        promotionCode,
      );
      return `${value} ${vatInfo}`;
    }
    if (!isFree && !hasPromotion) {
      const value = newFormatPrice(
        showPricesWithoutVAT ? priceTotalVAT : priceTotalNoVAT,
        true,
        priceCode,
      );
      return `${value} ${vatInfo}`;
    }
    return '';
  }, [
    t,
    isFree,
    hasPromotion,
    showPricesWithoutVAT,
    promotionTotalVAT,
    promotionTotalNoVAT,
    priceTotalVAT,
    priceTotalNoVAT,
  ]);

  // eslint-disable-next-line react/prop-types
  function PriceDisplayWrapper({ children }) {
    return withWrapper ? (
      <div className="price-display-wrapper">{children}</div>
    ) : (
      <>{children}</>
    );
  }

  if (!hasPromotion && isFree && showFree) {
    return null;
  }

  return (
    <PriceDisplayWrapper>
      <div className="price-display-container">
        <div className="main-price">
          {hasPromotion && <div className="previous">{previousPrice}</div>}
          <div
            className={`shopCurrent ${
              (hasPromotion || isFree) && 'current-promo'
            }`}
          >
            {frequency
              ? `${currentPrice} / ${t(`subscription.${frequency}`)}`
              : currentPrice}
          </div>
        </div>
        {!isFree && <div className="minor-price">{minorTotal}</div>}
      </div>
    </PriceDisplayWrapper>
  );
}

PriceDisplay.propTypes = {
  t: PropTypes.func.isRequired,
  userPrice: PropTypes.shape(PriceProp.propType),
  userPromotionPrice: PropTypes.shape(PriceProp.propType),
  type: PropTypes.string,
  showFree: PropTypes.bool,
  frequency: PropTypes.string,
  withWrapper: PropTypes.bool,
};
PriceDisplay.defaultProps = {
  userPrice: null,
  userPromotionPrice: null,
  type: null,
  showFree: true,
  frequency: '',
  withWrapper: false,
};

export default translate(PriceDisplay);
